<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} SNMP Metric</h1>
    </div>

    <form
      @submit.prevent="submitForm"
      class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <!-- Metric Name -->
      <div class="flex flex-col">
        <label for="name" class="mb-1 font-medium">
          Metric Name<span class="text-red-500">*</span>
        </label>
        <input
          id="name"
          v-model="formData.name"
          type="text"
          required
          placeholder="e.g., sysUpTime, ifHCInOctets"
          :class="inputClass('name')"
        />
        <p class="text-sm text-gray-500 mt-1">Prometheus metric name</p>
        <FieldError :message="fieldErrors.name" />
      </div>

      <!-- OID -->
      <div class="flex flex-col">
        <label for="oid" class="mb-1 font-medium">
          OID<span class="text-red-500">*</span>
        </label>
        <input
          id="oid"
          v-model="formData.oid"
          type="text"
          required
          placeholder="e.g., 1.3.6.1.2.1.1.3.0"
          :class="inputClass('oid')"
        />
        <p class="text-sm text-gray-500 mt-1">SNMP OID in numeric dot notation</p>
        <FieldError :message="fieldErrors.oid" />
      </div>

      <!-- Type -->
      <div class="flex flex-col">
        <label for="type" class="mb-1 font-medium">
          Type<span class="text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="formData.type"
          :options="metricTypes"
          valueKey="value"
          labelKey="label"
          placeholder="Select type"
          :error="!!fieldErrors.type"
        />
        <p class="text-sm text-gray-500 mt-1">Prometheus metric type or SNMP data type</p>
        <FieldError :message="fieldErrors.type" />
      </div>

      <!-- Help Text -->
      <div class="flex flex-col">
        <label for="help_text" class="mb-1 font-medium">
          Help Text<span class="text-red-500">*</span>
        </label>
        <input
          id="help_text"
          v-model="formData.help_text"
          type="text"
          required
          placeholder="e.g., System uptime in hundredths of seconds"
          :class="inputClass('help_text')"
        />
        <p class="text-sm text-gray-500 mt-1">Description of what this metric measures</p>
        <FieldError :message="fieldErrors.help_text" />
      </div>

      <!-- Indexes (for table OIDs) -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium">
          Indexes <span class="text-gray-500 text-sm">(optional, for table OIDs)</span>
        </label>
        <div class="space-y-2">
          <div
            v-for="(index, i) in formData.indexes"
            :key="i"
            class="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700 p-2 rounded"
          >
            <input
              v-model="index.labelname"
              type="text"
              placeholder="Label name (e.g., ifIndex)"
              class="flex-1 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
            />
            <GlassSelect
              v-model="index.type"
              :options="['gauge', 'Integer32', 'DisplayString']"
              placeholder="Type"
              class="!w-40"
            />
            <button type="button" @click="removeIndex(i)" class="text-red-500 hover:text-red-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <button
            type="button"
            @click="addIndex"
            class="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            Add Index
          </button>
        </div>
        <p class="text-sm text-gray-500 mt-1">Define index labels for table OIDs</p>
      </div>

      <!-- Lookups (for label enrichment) -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium">
          Lookups <span class="text-gray-500 text-sm">(optional, for label enrichment)</span>
        </label>
        <div class="space-y-3">
          <div
            v-for="(lookup, i) in formData.lookups"
            :key="i"
            class="bg-gray-50 dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600"
          >
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium">Lookup {{ i + 1 }}</span>
              <button type="button" @click="removeLookup(i)" class="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-xs text-gray-500">Label Name</label>
                <input
                  v-model="lookup.labelname"
                  type="text"
                  placeholder="e.g., ifName"
                  class="w-full border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label class="text-xs text-gray-500">OID</label>
                <input
                  v-model="lookup.oid"
                  type="text"
                  placeholder="e.g., 1.3.6.1.2.1.31.1.1.1.1"
                  class="w-full border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label class="text-xs text-gray-500">Type</label>
                <GlassSelect
                  v-model="lookup.type"
                  :options="['DisplayString', 'gauge', 'Integer32', 'PhysAddress48']"
                  placeholder="Type"
                />
              </div>
              <div>
                <label class="text-xs text-gray-500">Source Labels (comma-separated)</label>
                <input
                  v-model="lookup.labelsStr"
                  type="text"
                  placeholder="e.g., ifIndex"
                  class="w-full border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            @click="addLookup"
            class="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            Add Lookup
          </button>
        </div>
        <p class="text-sm text-gray-500 mt-1">Define lookups for enriching labels with descriptive names</p>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button type="button" @click="goBack" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          Cancel
        </button>
        <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          {{ isEditMode ? 'Update' : 'Create' }} Metric
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createSNMPMetric, getSNMPMetricById, updateSNMPMetric } from "@/services/snmpProfileService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddSNMPMetric",
  components: { FieldError, PageError, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      formData: {
        name: "",
        oid: "",
        type: "gauge",
        help_text: "",
        indexes: [],
        lookups: [],
      },
      metricTypes: [
        { value: "gauge", label: "Gauge" },
        { value: "counter", label: "Counter" },
        { value: "DisplayString", label: "DisplayString" },
        { value: "PhysAddress48", label: "PhysAddress48" },
        { value: "Float", label: "Float" },
        { value: "Integer", label: "Integer" },
        { value: "InetAddressIPv4", label: "InetAddressIPv4" },
        { value: "InetAddressIPv6", label: "InetAddressIPv6" },
        { value: "Bits", label: "Bits" },
        { value: "EnumAsInfo", label: "EnumAsInfo" },
        { value: "EnumAsStateSet", label: "EnumAsStateSet" },
      ],
      canAdd: false,
      isLoading: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    profileId() {
      return this.$route.params.profileId;
    },
    metricId() {
      return this.$route.params.metricId;
    },
    isEditMode() {
      return !!this.metricId;
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    if (this.isEditMode) {
      await this.loadExistingMetric();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add SNMP Metric");
      }
    },
    async loadExistingMetric() {
      this.isLoading = true;
      try {
        const metric = await getSNMPMetricById(this.metricId, this.currentSchema);
        this.formData.name = metric.name;
        this.formData.oid = metric.oid;
        this.formData.type = metric.type;
        this.formData.help_text = metric.help_text;
        this.formData.indexes = metric.indexes || [];
        // Convert lookups labels array to comma-separated string for editing
        this.formData.lookups = (metric.lookups || []).map((l) => ({
          ...l,
          labelsStr: l.labels ? l.labels.join(", ") : "",
        }));
      } catch (error) {
        console.error("Error loading metric:", error);
        alert("Failed to load metric data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        "border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600",
        this.fieldErrors[field] ? "border-red-500 ring-2 ring-red-500" : "border-gray-300 focus:ring-blue-500",
      ];
    },
    goBack() {
      this.$router.push({ name: "SNMPProfileDetails", params: { id: this.profileId }, query: this.tenant.addSchemaParam() });
    },
    addIndex() {
      this.formData.indexes.push({ labelname: "", type: "gauge" });
    },
    removeIndex(index) {
      this.formData.indexes.splice(index, 1);
    },
    addLookup() {
      this.formData.lookups.push({ labelname: "", oid: "", type: "DisplayString", labelsStr: "" });
    },
    removeLookup(index) {
      this.formData.lookups.splice(index, 1);
    },
    async submitForm() {
      this.fieldErrors = {};

      // Validate OID format
      const oidRegex = /^\d+(\.\d+)+$/;
      if (!oidRegex.test(this.formData.oid)) {
        this.fieldErrors.oid = "OID must be numeric with dots (e.g., 1.3.6.1.2.1.1.3.0)";
        return;
      }

      // Prepare indexes (filter out empty ones)
      const indexes = this.formData.indexes
        .filter((i) => i.labelname)
        .map((i) => ({ labelname: i.labelname, type: i.type }));

      // Prepare lookups (filter out empty ones, convert labelsStr to array)
      const lookups = this.formData.lookups
        .filter((l) => l.labelname && l.oid)
        .map((l) => ({
          labelname: l.labelname,
          oid: l.oid,
          type: l.type,
          labels: l.labelsStr ? l.labelsStr.split(",").map((s) => s.trim()).filter((s) => s) : [],
        }));

      // Validate lookup OIDs
      for (const lookup of lookups) {
        if (!oidRegex.test(lookup.oid)) {
          this.fieldErrors.lookups = `Invalid OID format in lookup "${lookup.labelname}"`;
          return;
        }
      }

      const data = {
        profile: parseInt(this.profileId),
        name: this.formData.name,
        oid: this.formData.oid,
        type: this.formData.type,
        help_text: this.formData.help_text,
        indexes: indexes.length > 0 ? indexes : null,
        lookups: lookups.length > 0 ? lookups : null,
      };

      try {
        if (this.isEditMode) {
          await updateSNMPMetric(this.metricId, data, this.currentSchema);
        } else {
          await createSNMPMetric(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            this.fieldErrors[key] = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
          });
        }
        console.error(`Error ${this.isEditMode ? "updating" : "creating"} metric:`, error);
      }
    },
  },
};
</script>
